import { SessionType, WebSessionType } from 'src/api/learningSessions/types';
import { ROUTES } from 'src/constants/routes.const';
import { BLOB_PRONUNCIATION_QUEUE_STORAGE_KEY } from 'src/constants/storageKeys.const';
import { stringifyUrl } from 'src/utils/stringifyUrl';
import { getLocalStorageEntry, setLocalStorageEntry } from 'src/utils/webStorageUtils';

export const BLOB_PRONUNCIATION_QUEUE_SOURCE = 'blob_learn_queue';
export const PRONUNCIATION_SESSION_WORD_COUNT = 5;
const SESSIONS_BETWEEN_PRONUNCIATION = 2;
const SESSION_HISTORY_LIMIT = 100;

type QueueState = {
  completedSessions: string[];
  resolvedPronunciationSessions: string[];
  sessionsSincePronunciation: number;
};

export const isBlobQueueSessionType = (sessionType: WebSessionType) =>
  [SessionType.LEARN, SessionType.REVIEW, SessionType.MIXED].includes(sessionType);

export const blobPronunciationQueueKey = (userId: number, languagePairId: number) =>
  `${BLOB_PRONUNCIATION_QUEUE_STORAGE_KEY}:${userId}:${languagePairId}`;

const readQueue = (userId: number, languagePairId: number): QueueState => {
  try {
    const state = JSON.parse(
      getLocalStorageEntry(blobPronunciationQueueKey(userId, languagePairId)) ?? 'null',
    ) as QueueState | null;
    if (
      state &&
      Number.isInteger(state.sessionsSincePronunciation) &&
      state.sessionsSincePronunciation >= 0 &&
      state.sessionsSincePronunciation <= SESSIONS_BETWEEN_PRONUNCIATION &&
      Array.isArray(state.completedSessions) &&
      state.completedSessions.every(id => typeof id === 'string') &&
      Array.isArray(state.resolvedPronunciationSessions) &&
      state.resolvedPronunciationSessions.every(id => typeof id === 'string')
    ) {
      return state;
    }
  } catch {
    // Unavailable or damaged browser storage must not prevent learning.
  }
  return {
    completedSessions: [],
    resolvedPronunciationSessions: [],
    sessionsSincePronunciation: 0,
  };
};

const writeQueue = (userId: number, languagePairId: number, state: QueueState) => {
  setLocalStorageEntry(blobPronunciationQueueKey(userId, languagePairId), JSON.stringify(state));
};

export const isBlobPronunciationDue = (userId: number, languagePairId: number): boolean =>
  readQueue(userId, languagePairId).sessionsSincePronunciation >= SESSIONS_BETWEEN_PRONUNCIATION;

export const recordBlobLearningSessionCompletion = (
  userId: number,
  languagePairId: number,
  sessionId: string,
  sessionType: WebSessionType,
): void => {
  if (!sessionId || !isBlobQueueSessionType(sessionType)) return;
  const state = readQueue(userId, languagePairId);
  if (state.completedSessions.includes(sessionId)) return;
  writeQueue(userId, languagePairId, {
    ...state,
    completedSessions: [...state.completedSessions, sessionId].slice(-SESSION_HISTORY_LIMIT),
    sessionsSincePronunciation: Math.min(
      SESSIONS_BETWEEN_PRONUNCIATION,
      state.sessionsSincePronunciation + 1,
    ),
  });
};

export const resolveBlobPronunciationSlot = (
  userId: number,
  languagePairId: number,
  sessionId: string,
): void => {
  const state = readQueue(userId, languagePairId);
  if (state.resolvedPronunciationSessions.includes(sessionId)) return;
  writeQueue(userId, languagePairId, {
    ...state,
    sessionsSincePronunciation: 0,
    resolvedPronunciationSessions: [...state.resolvedPronunciationSessions, sessionId].slice(
      -SESSION_HISTORY_LIMIT,
    ),
  });
};

/** Clears a due offer when pronunciation is presented so Back cannot re-hijack Learn. */
export const clearBlobPronunciationDue = (userId: number, languagePairId: number): void => {
  const state = readQueue(userId, languagePairId);
  if (state.sessionsSincePronunciation === 0) return;
  writeQueue(userId, languagePairId, {
    ...state,
    sessionsSincePronunciation: 0,
  });
};

export const parseQueueId = (value: string | string[] | undefined): number | null => {
  if (typeof value !== 'string' || !/^[1-9]\d*$/.test(value)) return null;
  const id = Number(value);
  return Number.isSafeInteger(id) ? id : null;
};

export const getBlobPronunciationQueueUrl = (languagePairId: number, blobId: number | null) =>
  stringifyUrl(ROUTES.ai.dialog, {
    type: 'pronunciation',
    source: BLOB_PRONUNCIATION_QUEUE_SOURCE,
    language_pair_id: languagePairId,
    back: ROUTES.path,
    ...(blobId !== null && { blob_id: blobId }),
  });
